import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Loader2, TriangleAlert } from "lucide-react";
import { useCart } from "../context/CartContext";
import { API } from "../lib/shop";

const MAX_POLLS = 10;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const { clearCart } = useCart();
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | timeout | error
  const polls = useRef(0);
  const started = useRef(false);

  useEffect(() => {
    if (started.current || !sessionId) return;
    started.current = true;

    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          clearCart();
          navigate(`/order/${data.order_id}`, { replace: true });
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setState("error");
          return;
        }
        polls.current += 1;
        if (polls.current >= MAX_POLLS) {
          setState("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch {
        setState("error");
      }
    };
    poll();
  }, [sessionId, clearCart, navigate]);

  if (!sessionId) {
    return (
      <div className="mx-auto max-w-md px-4 py-24 text-center">
        <p className="text-lg font-semibold">Missing payment session.</p>
        <Link to="/cart" className="mt-2 inline-block text-secondary underline">Back to cart</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-4 py-24 text-center" data-testid="payment-success-page">
      {state === "checking" && (
        <>
          <Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" />
          <h1 className="mt-4 text-2xl font-bold">Confirming your payment…</h1>
          <p className="mt-2 text-sm text-muted-foreground">Please don't close this page — this only takes a few seconds.</p>
        </>
      )}
      {state === "timeout" && (
        <>
          <TriangleAlert className="mx-auto h-10 w-10 text-[#f59e0b]" />
          <h1 className="mt-4 text-2xl font-bold">Payment is processing</h1>
          <p className="mt-2 text-sm text-muted-foreground" data-testid="payment-timeout-message">
            Your payment is still being confirmed. Check My Orders shortly, or contact us if the charge appears on your statement.
          </p>
          <Link to="/my-orders" className="mt-6 inline-block rounded-sm bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground">My Orders</Link>
        </>
      )}
      {state === "error" && (
        <>
          <TriangleAlert className="mx-auto h-10 w-10 text-destructive" />
          <h1 className="mt-4 text-2xl font-bold">Payment not completed</h1>
          <p className="mt-2 text-sm text-muted-foreground" data-testid="payment-error-message">
            The payment session expired or failed. Your cart is untouched — you can try again.
          </p>
          <Link to="/cart" className="mt-6 inline-block rounded-sm bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground">Back to cart</Link>
        </>
      )}
    </div>
  );
}
